/* eslint-disable max-lines */

import { cloneElement, isValidElement, useEffect, useMemo, useState } from "react"
import type { ReactElement } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { ToolApproval } from "@/api/models"

import {
  Calendar,
  Check,
  ChevronDown,
  Clock,
  ExternalLink,
  Loader2,
  Mail,
  MapPin,
  Users,
  Video,
  Wrench,
  X,
} from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { useApprovalCountdown } from "@/features/chat/hooks/use-approval-countdown"
import {
  type DurableToolApprovalBinding,
  useToolApprovalActions,
} from "@/features/chat/hooks/use-tool-approval-actions"
import { logger } from "@/lib/logger"
import { cn } from "@/lib/utils"
import { getToolMetadata } from "@/utils/tool-metadata"
import { filterNonEmptyParams } from "@/utils/tool-params"

const statusToVariant: Record<ToolApproval["status"], { label: string; className: string }> = {
  pending: {
    label: "Pending",
    className: "bg-warning/30 text-warning-ink ",
  },
  approved: {
    label: "Approved",
    className: "bg-success/15 border border-success text-foreground",
  },
  denied: {
    label: "Denied",
    className: "bg-destructive/15 border border-destructive text-foreground",
  },
  expired: {
    label: "Expired",
    className: "bg-muted text-muted-foreground",
  },
  cancelled: {
    label: "Cancelled",
    className: "bg-muted text-muted-foreground",
  },
}

function formatToolName(toolName: string): string {
  return toolName
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}

function formatArgumentKey(key: string): string {
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}

function formatSummary(summary: string): string {
  // Format action names in summaries like "M365: send_email" → "M365: Send Email"
  return summary.replace(/:\s*([a-z_]+)/gi, (_, action: string) => {
    if (action.includes("_")) {
      const formatted = action
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
      return `: ${formatted}`
    }
    return `: ${action}`
  })
}

function isUrl(value: string): boolean {
  return /^https?:\/\//i.test(value)
}

function extractDomain(url: string): string {
  try {
    return new URL(url).hostname
  } catch {
    return url
  }
}

function formatNumberValue(key: string, value: number): string {
  const lowerKey = key.toLowerCase()
  if (lowerKey.includes("result") || lowerKey.includes("top") || lowerKey.includes("count")) {
    return `${value} result${value !== 1 ? "s" : ""}`
  }
  if (lowerKey.includes("timeout") || lowerKey.includes("sec")) {
    return `${value} second${value !== 1 ? "s" : ""}`
  }
  return String(value)
}

type ToolIconProps = {
  toolName: string
  className?: string
}

function ToolIcon({ toolName, className }: ToolIconProps) {
  const toolMeta = getToolMetadata(toolName)
  if (toolMeta?.icon && isValidElement(toolMeta.icon)) {
    const iconElement = toolMeta.icon as ReactElement<{ className?: string }>
    return cloneElement(iconElement, { className })
  }
  return <Wrench className={className} />
}

type ArgumentValueProps = {
  keyName: string
  value: unknown
}

function ArgumentValue({ keyName, value }: ArgumentValueProps) {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground/60 italic">not set</span>
  }

  if (typeof value === "string") {
    if (isUrl(value)) {
      return (
        <a
          className="text-primary inline-flex items-center gap-1 hover:underline"
          href={value}
          rel="noopener noreferrer"
          target="_blank"
        >
          {extractDomain(value)}
          <ExternalLink className="size-3" />
        </a>
      )
    }
    const isQueryLike = keyName.toLowerCase().includes("query")
    if (isQueryLike) {
      return <span className="text-foreground font-medium">"{value}"</span>
    }
    return <span className="text-foreground">{value}</span>
  }

  if (typeof value === "number") {
    return <span className="text-foreground">{formatNumberValue(keyName, value)}</span>
  }

  if (typeof value === "boolean") {
    return <span className="text-foreground">{value ? "Yes" : "No"}</span>
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-muted-foreground/60 italic">empty</span>
    }
    const allUrls = value.every((item) => typeof item === "string" && isUrl(item))
    if (allUrls) {
      return (
        <ul className="mt-1 space-y-1">
          {value.map((url, index) => (
            <li key={index}>
              <a
                className="text-primary inline-flex items-center gap-1 text-sm hover:underline"
                href={url as string}
                rel="noopener noreferrer"
                target="_blank"
              >
                {extractDomain(url as string)}
                <ExternalLink className="size-3" />
              </a>
            </li>
          ))}
        </ul>
      )
    }
    if (
      value.length <= 3 &&
      value.every((item) => typeof item === "string" || typeof item === "number")
    ) {
      return <span className="text-foreground">{value.join(", ")}</span>
    }
    return (
      <ul className="text-foreground mt-1 list-inside list-disc space-y-0.5">
        {value.slice(0, 5).map((item, index) => (
          <li key={index} className="text-sm">
            {typeof item === "object" ? JSON.stringify(item) : String(item)}
          </li>
        ))}
        {value.length > 5 && (
          <li className="text-muted-foreground text-sm">...and {value.length - 5} more</li>
        )}
      </ul>
    )
  }

  if (typeof value === "object") {
    return (
      <span className="text-muted-foreground italic">{Object.keys(value).length} properties</span>
    )
  }

  return <span className="text-foreground">{String(value)}</span>
}

/**
 * Arguments of a call that is asked only because personal content is in the chat. The model chose
 * them after reading text somebody else may have written, so nothing here is a link: addresses are
 * shown as text for the reader to judge, never as something one click away.
 */
function PlainToolArguments({ args }: { args: Record<string, unknown> }) {
  const entries = Object.entries(filterNonEmptyParams(args))
  if (entries.length === 0) {
    return <p className="text-muted-foreground text-sm italic">No parameters</p>
  }
  return (
    <dl className="space-y-2" data-testid="plain-tool-arguments">
      {entries.map(([key, value]) => (
        <div key={key} className="flex flex-col gap-0.5 text-sm sm:flex-row sm:gap-3">
          <dt className="text-muted-foreground min-w-[100px] shrink-0">{formatArgumentKey(key)}</dt>
          <dd className="text-foreground flex-1 break-all">
            {typeof value === "string" ? value : JSON.stringify(value)}
          </dd>
        </div>
      ))}
    </dl>
  )
}

type ToolArgumentsDisplayProps = {
  args: Record<string, unknown>
}

function ToolArgumentsDisplay({ args }: ToolArgumentsDisplayProps) {
  const filteredArgs = filterNonEmptyParams(args)
  const entries = Object.entries(filteredArgs)

  if (entries.length === 0) {
    return <p className="text-muted-foreground text-sm italic">No parameters</p>
  }

  return (
    <div className="space-y-2">
      {entries.map(([key, value]) => (
        <div key={key} className="flex flex-col gap-0.5 text-sm sm:flex-row sm:gap-3">
          <span className="text-muted-foreground min-w-[100px] shrink-0">
            {formatArgumentKey(key)}
          </span>
          <div className="flex-1 break-words">
            <ArgumentValue keyName={key} value={value} />
          </div>
        </div>
      ))}
    </div>
  )
}

type M365EmailParams = {
  to?: string[]
  cc?: string[]
  bcc?: string[]
  subject?: string
  body?: string
  body_type?: "text" | "html"
}

type CalendarDateTimeInput = string | { dateTime?: string; timeZone?: string }

type M365AttendeeInput =
  | string
  | {
      email?: string
      address?: string
      name?: string
      type?: "required" | "optional"
      emailAddress?: { address?: string; name?: string }
    }

type NormalizedAttendee = {
  email?: string
  name?: string
  type: "required" | "optional"
}

type M365CalendarParams = {
  subject?: string
  body?: string | { content?: string; contentType?: string }
  start?: CalendarDateTimeInput
  end?: CalendarDateTimeInput
  timezone?: string
  location?: string | { displayName?: string }
  attendees?: M365AttendeeInput[]
  is_online_meeting?: boolean
  isOnlineMeeting?: boolean
  isAllDay?: boolean
}

type OriginalEventData = {
  subject?: string
  body?: string
  start?: CalendarDateTimeInput
  end?: CalendarDateTimeInput
  location?: string
  attendees?: NormalizedAttendee[]
  isOnlineMeeting?: boolean
  isAllDay?: boolean
  sensitivity?: string
  showAs?: string
  importance?: string
  categories?: string[]
  reminderMinutesBeforeStart?: number
}

type M365EditCalendarParams = M365CalendarParams & {
  event_id?: string
  event_name?: string
  sensitivity?: string
  showAs?: string
  importance?: string
  categories?: string[]
  reminderMinutesBeforeStart?: number
  _original?: OriginalEventData
}

type CalendarEventPreviewParams = {
  subject?: string
  body?: string
  start?: string
  end?: string
  timezone?: string
  location?: string
  attendees: NormalizedAttendee[]
  isOnlineMeeting?: boolean
  isAllDay?: boolean
}

function RecipientPills({ emails, label }: { emails: string[]; label: string }) {
  if (emails.length === 0) return null
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-muted-foreground text-xs font-medium">{label}:</span>
      {emails.map((email) => (
        <span
          key={email}
          className="bg-primary/10 text-foreground inline-flex items-center rounded-md px-2 py-0.5 text-xs"
        >
          {email}
        </span>
      ))}
    </div>
  )
}

function extractDateTime(value?: CalendarDateTimeInput) {
  if (!value) {
    return { dateTime: undefined, timeZone: undefined }
  }
  if (typeof value === "string") {
    return { dateTime: value, timeZone: undefined }
  }
  return { dateTime: value.dateTime, timeZone: value.timeZone }
}

function normalizeLocation(location?: M365CalendarParams["location"]) {
  if (typeof location === "string") {
    return location
  }
  if (location && typeof location === "object") {
    return (location as { displayName?: string }).displayName
  }
  return undefined
}

function normalizeBody(body?: M365CalendarParams["body"]) {
  if (typeof body === "string") {
    return body
  }
  if (body && typeof body === "object") {
    return (body as { content?: string }).content
  }
  return undefined
}

function buildCalendarPreviewParams(params: M365CalendarParams): CalendarEventPreviewParams {
  const { dateTime: start, timeZone: startTimeZone } = extractDateTime(params.start)
  const { dateTime: end, timeZone: endTimeZone } = extractDateTime(params.end)
  const timezone = params.timezone || startTimeZone || endTimeZone
  return {
    subject: params.subject,
    body: normalizeBody(params.body),
    start,
    end,
    timezone,
    location: normalizeLocation(params.location),
    attendees: normalizeAttendees(params.attendees),
    isOnlineMeeting: params.is_online_meeting ?? params.isOnlineMeeting,
    isAllDay: params.isAllDay,
  }
}

function EmailPreview({ params }: { params: M365EmailParams }) {
  const { to = [], cc = [], bcc = [], subject, body, body_type: bodyType } = params
  const isHtml = bodyType === "html"

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2">
        <Mail className="text-primary/60 mt-0.5 size-4 shrink-0" />
        <div className="min-w-0 flex-1 space-y-2">
          <RecipientPills emails={to} label="To" />
          <RecipientPills emails={cc} label="Cc" />
          <RecipientPills emails={bcc} label="Bcc" />
        </div>
      </div>
      {subject && (
        <div className="border-border/50 bg-muted/20 rounded-md border p-3">
          <p className="text-foreground text-sm font-medium">{subject}</p>
        </div>
      )}
      {body && (
        <div className="border-border/50 bg-muted/30 max-h-48 overflow-y-auto rounded-md border p-3">
          <p className="text-muted-foreground text-sm break-words whitespace-pre-wrap">
            {isHtml ? <span className="italic">[HTML content]</span> : body}
          </p>
        </div>
      )}
    </div>
  )
}

function formatEventDateTime(isoString: string, timezone?: string): string {
  if (!isoString) return ""
  try {
    const date = new Date(isoString)
    return date.toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: timezone,
      timeZoneName: timezone ? undefined : "short",
    })
  } catch {
    return isoString
  }
}

function normalizeAttendee(attendee: M365AttendeeInput): NormalizedAttendee | null {
  if (typeof attendee === "string") {
    return { email: attendee, name: undefined, type: "required" }
  }

  const email = attendee.email || attendee.address || attendee.emailAddress?.address
  const name = attendee.name || attendee.emailAddress?.name
  const type = attendee.type === "optional" ? "optional" : "required"

  if (!email && !name) {
    return null
  }

  return { email, name, type }
}

function normalizeAttendees(attendees: M365AttendeeInput[] = []): NormalizedAttendee[] {
  return attendees
    .map(normalizeAttendee)
    .filter((value): value is NormalizedAttendee => value !== null)
}

// Diff display components for before/after comparison
type DiffValueProps = {
  label: string
  icon?: React.ReactNode
  oldValue: string | undefined | null
  newValue: string | undefined | null
}

function DiffValue({ label, icon, oldValue, newValue }: DiffValueProps) {
  const hasOld = oldValue !== undefined && oldValue !== null && oldValue !== ""
  const hasNew = newValue !== undefined && newValue !== null && newValue !== ""

  // No change or both empty
  if (oldValue === newValue) return null
  if (!hasOld && !hasNew) return null

  return (
    <div className="flex items-start gap-2 text-sm">
      {icon && <span className="text-primary/60 mt-0.5 shrink-0">{icon}</span>}
      <div className="flex-1 space-y-1">
        <span className="text-muted-foreground text-xs font-medium">{label}</span>
        <div className="flex flex-wrap items-center gap-1.5">
          {hasOld && <span className="text-muted-foreground/70 line-through">{oldValue}</span>}
          <span className="text-muted-foreground">→</span>
          {hasNew ? (
            <span className="text-foreground font-medium">{newValue}</span>
          ) : (
            <span className="text-muted-foreground/60 italic">none</span>
          )}
        </div>
      </div>
    </div>
  )
}

type AttendeesDiffProps = {
  original: NormalizedAttendee[] | undefined
  updated: NormalizedAttendee[]
}

function AttendeesDiff({ original, updated }: AttendeesDiffProps) {
  const oldCount = original?.length ?? 0
  const newCount = updated.length

  // No change
  if (oldCount === newCount && oldCount === 0) return null

  const formatCount = (count: number) => {
    if (count === 0) return "none"
    return `${count} attendee${count !== 1 ? "s" : ""}`
  }

  // If counts are different, show the diff
  if (oldCount !== newCount) {
    return (
      <div className="flex items-start gap-2 text-sm">
        <Users className="text-primary/60 mt-0.5 size-4 shrink-0" />
        <div className="flex-1 space-y-1">
          <span className="text-muted-foreground text-xs font-medium">Attendees</span>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-muted-foreground/70 line-through">{formatCount(oldCount)}</span>
            <span className="text-muted-foreground">→</span>
            {newCount > 0 ? (
              <span className="text-foreground font-medium">{formatCount(newCount)}</span>
            ) : (
              <span className="text-muted-foreground/60 italic">none</span>
            )}
          </div>
          {newCount > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {updated.map((att, idx) => (
                <span
                  key={att.email ?? att.name ?? idx}
                  className="bg-primary/10 text-foreground inline-flex items-center rounded-md px-2 py-0.5 text-xs"
                >
                  {att.name || att.email}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  // Same count but potentially different people - just show updated list
  if (newCount > 0) {
    return (
      <div className="flex items-start gap-2 text-sm">
        <Users className="text-primary/60 mt-0.5 size-4 shrink-0" />
        <div className="flex flex-1 flex-wrap gap-1">
          {updated.map((att, idx) => (
            <span
              key={att.email ?? att.name ?? idx}
              className="bg-primary/10 text-foreground inline-flex items-center rounded-md px-2 py-0.5 text-xs"
            >
              {att.name || att.email}
            </span>
          ))}
        </div>
      </div>
    )
  }

  return null
}

type DateTimeDiffProps = {
  originalStart?: CalendarDateTimeInput
  originalEnd?: CalendarDateTimeInput
  newStart?: string
  newEnd?: string
  timezone?: string
}

function DateTimeDiff({
  originalStart,
  originalEnd,
  newStart,
  newEnd,
  timezone,
}: DateTimeDiffProps) {
  const { dateTime: origStartDt } = extractDateTime(originalStart)
  const { dateTime: origEndDt } = extractDateTime(originalEnd)

  const formatDt = (dt: string | undefined) => (dt ? formatEventDateTime(dt, timezone) : undefined)

  const origStartFmt = formatDt(origStartDt)
  const origEndFmt = formatDt(origEndDt)
  const newStartFmt = formatDt(newStart)
  const newEndFmt = formatDt(newEnd)

  const hasStartChange = newStart && origStartFmt !== newStartFmt
  const hasEndChange = newEnd && origEndFmt !== newEndFmt

  if (!hasStartChange && !hasEndChange) {
    // No change, show current values if available
    if (newStart || newEnd) {
      return (
        <div className="flex items-center gap-2 text-sm">
          <Calendar className="text-primary/60 size-4 shrink-0" />
          <span className="text-foreground">
            {newStart && formatEventDateTime(newStart, timezone)}
            {newStart && newEnd && " → "}
            {newEnd && formatEventDateTime(newEnd, timezone)}
          </span>
        </div>
      )
    }
    return null
  }

  return (
    <div className="flex items-start gap-2 text-sm">
      <Calendar className="text-primary/60 mt-0.5 size-4 shrink-0" />
      <div className="flex-1 space-y-1">
        <span className="text-muted-foreground text-xs font-medium">Date & Time</span>
        <div className="flex flex-wrap items-center gap-1.5">
          {origStartFmt && (
            <span className="text-muted-foreground/70 line-through">
              {origStartFmt}
              {origEndFmt && ` → ${origEndFmt}`}
            </span>
          )}
          <span className="text-muted-foreground">→</span>
          <span className="text-foreground font-medium">
            {newStartFmt}
            {newEndFmt && ` → ${newEndFmt}`}
          </span>
        </div>
      </div>
    </div>
  )
}

// Expandable text component for long content
type ExpandableTextProps = {
  text: string
  maxLength?: number
  className?: string
}

function ExpandableText({ text, maxLength = 150, className }: ExpandableTextProps) {
  const [expanded, setExpanded] = useState(false)
  const needsTruncation = text.length > maxLength

  if (!needsTruncation) {
    return <span className={className}>{text}</span>
  }

  return (
    <span className={className}>
      {expanded ? text : `${text.slice(0, maxLength)}...`}
      <button
        className="text-primary ml-1 text-xs font-medium hover:underline"
        type="button"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? "Show less" : "Show more"}
      </button>
    </span>
  )
}

// Body/description diff with expandable text support
type BodyDiffProps = {
  oldBody?: string | null
  newBody?: string | null
}

function BodyDiff({ oldBody, newBody }: BodyDiffProps) {
  const hasOld = oldBody !== undefined && oldBody !== null && oldBody !== ""
  const hasNew = newBody !== undefined && newBody !== null && newBody !== ""

  // No change or both empty
  if (oldBody === newBody) return null
  if (!hasOld && !hasNew) return null

  return (
    <div className="space-y-2">
      <span className="text-muted-foreground text-xs font-medium">Description</span>

      {hasOld && oldBody !== newBody && (
        <div className="border-border/30 bg-destructive/5 rounded-md border p-2">
          <p className="text-muted-foreground/70 text-xs line-through">
            <ExpandableText maxLength={100} text={oldBody} />
          </p>
        </div>
      )}

      {hasNew && (
        <div className="border-border/50 bg-muted/30 rounded-md border p-2">
          <p className="text-foreground text-sm whitespace-pre-wrap">
            <ExpandableText maxLength={150} text={newBody} />
          </p>
        </div>
      )}
      {!hasNew && hasOld && (
        <div className="border-border/30 bg-muted/20 rounded-md border p-2">
          <p className="text-muted-foreground/60 text-sm italic">Removed</p>
        </div>
      )}
    </div>
  )
}

function CalendarEventPreview({ params }: { params: CalendarEventPreviewParams }) {
  const { subject, body, start, end, timezone, location, attendees, isOnlineMeeting, isAllDay } =
    params
  const normalizedAttendees = attendees
  const requiredAttendees = normalizedAttendees.filter((attendee) => attendee.type !== "optional")
  const optionalAttendees = normalizedAttendees.filter((attendee) => attendee.type === "optional")

  return (
    <div className="space-y-3">
      {subject && (
        <div className="border-border/50 bg-primary/5 rounded-md border p-3">
          <p className="text-foreground text-sm font-semibold">{subject}</p>
        </div>
      )}
      {(start || end) && (
        <div className="flex items-center gap-2 text-sm">
          <Calendar className="text-primary/60 size-4 shrink-0" />
          <span className="text-foreground">
            {start && formatEventDateTime(start, timezone)}
            {start && end && " → "}
            {end && formatEventDateTime(end, timezone)}
          </span>
          {timezone && <span className="text-muted-foreground text-xs">({timezone})</span>}
        </div>
      )}
      {isAllDay && (
        <div className="flex items-center gap-2 text-sm">
          <Clock className="text-primary/60 size-4 shrink-0" />
          <span className="text-foreground">All-day event</span>
        </div>
      )}
      {location && (
        <div className="flex items-center gap-2 text-sm">
          <MapPin className="text-primary/60 size-4 shrink-0" />
          <span className="text-foreground">{location}</span>
        </div>
      )}
      {isOnlineMeeting && (
        <div className="flex items-center gap-2 text-sm">
          <Video className="text-primary size-4 shrink-0" />
          <span className="border-primary bg-primary/15 text-foreground rounded border px-2 py-0.5 text-xs font-medium">
            Teams Meeting
          </span>
        </div>
      )}
      {normalizedAttendees.length > 0 && (
        <div className="flex items-start gap-2 text-sm">
          <Users className="text-primary/60 mt-0.5 size-4 shrink-0" />
          <div className="flex-1 space-y-1.5">
            {requiredAttendees.length > 0 && (
              <div className="flex flex-wrap items-center gap-1">
                {requiredAttendees.map((attendee, index) => (
                  <span
                    key={attendee.email ?? attendee.name ?? `required-${index}`}
                    className="bg-primary/10 text-foreground inline-flex items-center rounded-md px-2 py-0.5 text-xs"
                  >
                    {attendee.name || attendee.email}
                  </span>
                ))}
              </div>
            )}
            {optionalAttendees.length > 0 && (
              <div className="flex flex-wrap items-center gap-1">
                <span className="text-muted-foreground text-xs">Optional:</span>
                {optionalAttendees.map((attendee, index) => (
                  <span
                    key={attendee.email ?? attendee.name ?? `optional-${index}`}
                    className="bg-muted text-muted-foreground inline-flex items-center rounded-md px-2 py-0.5 text-xs"
                  >
                    {attendee.name || attendee.email}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {body && (
        <div className="border-border/50 bg-muted/30 max-h-48 overflow-y-auto rounded-md border p-3">
          <p className="text-muted-foreground text-sm break-words whitespace-pre-wrap">{body}</p>
        </div>
      )}
    </div>
  )
}

function CalendarEventEditPreview({ params }: { params: M365EditCalendarParams }) {
  const original = params._original
  const normalizedParams = buildCalendarPreviewParams(params)
  const normalizedAttendees = normalizeAttendees(params.attendees)

  // Check which fields are actually being updated (present in the request)
  const isUpdating = (key: string) =>
    key in params && params[key as keyof M365EditCalendarParams] !== undefined

  // Check if we have original data for diff display
  const hasOriginal = Boolean(original)

  // Additional fields - only include if they're being updated
  const additionalFieldDiffs = [
    {
      key: "sensitivity",
      label: "Sensitivity",
      oldValue: original?.sensitivity,
      newValue: params.sensitivity,
    },
    {
      key: "showAs",
      label: "Show As",
      oldValue: original?.showAs,
      newValue: params.showAs,
    },
    {
      key: "importance",
      label: "Importance",
      oldValue: original?.importance,
      newValue: params.importance,
    },
    {
      key: "reminderMinutesBeforeStart",
      label: "Reminder",
      oldValue: original?.reminderMinutesBeforeStart?.toString(),
      newValue: params.reminderMinutesBeforeStart?.toString(),
    },
  ].filter(({ key }) => isUpdating(key))

  return (
    <div className="space-y-3">
      {/* Event header - show original name for context */}
      {(params.event_name || original?.subject || params.event_id) && (
        <div className="border-border/50 bg-primary/5 rounded-md border p-3">
          <p className="text-foreground text-sm font-semibold">
            {params.event_name || original?.subject || `Event ID: ${params.event_id}`}
          </p>
        </div>
      )}

      {/* Diff display when we have original data */}
      {hasOriginal ? (
        <div className="space-y-3">
          {/* Subject diff - only if subject is being updated */}
          {isUpdating("subject") && (
            <DiffValue
              icon={null}
              label="Subject"
              newValue={params.subject}
              oldValue={original?.subject}
            />
          )}

          {/* Date/Time diff - only if start or end is being updated */}
          {(isUpdating("start") || isUpdating("end")) && (
            <DateTimeDiff
              newEnd={normalizedParams.end}
              newStart={normalizedParams.start}
              originalEnd={original?.end}
              originalStart={original?.start}
              timezone={normalizedParams.timezone}
            />
          )}

          {/* Location diff - only if location is being updated */}
          {isUpdating("location") && (
            <DiffValue
              icon={<MapPin className="size-4" />}
              label="Location"
              newValue={normalizedParams.location}
              oldValue={original?.location}
            />
          )}

          {/* Online meeting diff */}
          {(isUpdating("is_online_meeting") || isUpdating("isOnlineMeeting")) && (
            <DiffValue
              icon={<Video className="size-4" />}
              label="Teams Meeting"
              newValue={normalizedParams.isOnlineMeeting ? "Enabled" : "Disabled"}
              oldValue={original?.isOnlineMeeting ? "Enabled" : "Disabled"}
            />
          )}

          {/* All-day diff */}
          {isUpdating("isAllDay") && (
            <DiffValue
              icon={<Clock className="size-4" />}
              label="All-day Event"
              newValue={normalizedParams.isAllDay ? "Yes" : "No"}
              oldValue={original?.isAllDay ? "Yes" : "No"}
            />
          )}

          {/* Attendees diff - only if attendees is being updated */}
          {isUpdating("attendees") && (
            <AttendeesDiff original={original?.attendees} updated={normalizedAttendees} />
          )}

          {/* Body/description diff - only if body is being updated */}
          {isUpdating("body") && (
            <BodyDiff newBody={normalizedParams.body} oldBody={original?.body} />
          )}

          {/* Additional field diffs */}
          {additionalFieldDiffs.length > 0 && (
            <div className="space-y-2">
              {additionalFieldDiffs.map((field) => (
                <DiffValue
                  key={field.key}
                  icon={null}
                  label={field.label}
                  newValue={field.newValue}
                  oldValue={field.oldValue}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        // Fallback to original preview when no _original data available
        <CalendarEventPreview params={normalizedParams} />
      )}
    </div>
  )
}

function getM365Action(args: Record<string, unknown>): string | null {
  return typeof args.action === "string" ? args.action : null
}

function getM365Parameters(args: Record<string, unknown>): Record<string, unknown> | null {
  if (args.parameters && typeof args.parameters === "object") {
    return args.parameters as Record<string, unknown>
  }
  return null
}

function ContextualParametersDisplay({ args }: { args: Record<string, unknown> }) {
  const action = getM365Action(args)
  const params = getM365Parameters(args)

  if (action === "send_email" && params) {
    return <EmailPreview params={params as M365EmailParams} />
  }

  if (action === "create_calendar_event" && params) {
    const previewParams = buildCalendarPreviewParams(params as M365CalendarParams)
    return <CalendarEventPreview params={previewParams} />
  }

  if (action === "edit_calendar_event" && params) {
    return <CalendarEventEditPreview params={params as M365EditCalendarParams} />
  }

  return <ToolArgumentsDisplay args={args} />
}

function getParametersSectionTitle(args: Record<string, unknown>): string {
  const action = getM365Action(args)
  if (action === "send_email") return "Email Details"
  if (action === "create_calendar_event") return "Event Details"
  if (action === "edit_calendar_event") return "Event Changes"
  return "Parameters"
}

type ToolApprovalCardProps = {
  approval: ToolApproval
  sessionId: string | null
  fallbackSessionId?: string | null
  className?: string
  showPosition?: boolean
  position?: number
  total?: number
  disabled?: boolean
  showShortcutsHint?: boolean
  /** See `ToolApprovalOverlay`'s own `durableTurn` doc comment — the same
   *  binding, already resolved by the overlay, so every card in a batch
   *  shares one `getToken` closure instead of each re-deriving it. */
  durable?: DurableToolApprovalBinding | null
}

export function ToolApprovalCard({
  approval,
  sessionId,
  fallbackSessionId,
  className,
  showPosition = false,
  position,
  total,
  disabled = false,
  showShortcutsHint = false,
  durable = null,
}: ToolApprovalCardProps) {
  const { t } = useTranslation()
  const { approve, deny } = useToolApprovalActions(sessionId, fallbackSessionId, durable)
  const isPersonalGate = approval.gate === "personal_sources"
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isExiting, setIsExiting] = useState(false)
  const [showRawJson, setShowRawJson] = useState(false)

  const countdown = useApprovalCountdown({
    createdAt: approval.created_at,
    timeoutSecs: approval.timeout_secs,
    status: approval.status,
  })

  const statusMeta = statusToVariant[approval.status]
  const displayToolName = useMemo(() => formatToolName(approval.tool), [approval.tool])
  const formattedArguments = useMemo(
    () => JSON.stringify(approval.arguments, null, 2),
    [approval.arguments]
  )

  useEffect(() => {
    if (approval.status !== "pending") {
      return
    }
    logger.info("ToolApprovalCard binding session IDs", {
      requestId: approval.request_id,
      actionSessionId: sessionId,
      fallbackSessionId: fallbackSessionId ?? null,
    })
  }, [approval.request_id, approval.status, fallbackSessionId, sessionId])

  const handleDecision = async (approved: boolean) => {
    // Defense in depth: the Approve button is disabled for this case, but
    // refuse here too rather than trust only the disabled attribute.
    if (approved && approval.argumentsElided) {
      return
    }
    setIsSubmitting(true)
    setErrorMessage(null)
    try {
      if (approved) {
        await approve(approval.request_id, approval.call_id)
        toast.success("Tool approved", {
          description: `${displayToolName} has been approved`,
        })
      } else {
        await deny(approval.request_id, approval.call_id)
        toast.success("Tool denied", {
          description: `${displayToolName} has been denied`,
        })
      }
      setIsExiting(true)
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : String(error))
      toast.error("Error", {
        description: error instanceof Error ? error.message : String(error),
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const isPending = approval.status === "pending"
  const canTakeAction =
    Boolean(sessionId || fallbackSessionId) && isPending && !isSubmitting && !disabled
  // The backend dropped `arguments` to fit its durable-event size budget, but
  // the tool still executes with its full, original arguments after approval
  // (issue #2513, Security Reviewer) -- Approve must not be offered without
  // seeing what's actually being run. Deny stays available: it never needs
  // to inspect arguments.
  const argumentsHidden = Boolean(approval.argumentsElided)
  const canApprove = canTakeAction && !argumentsHidden

  return (
    <div
      className={cn(
        "border-border bg-card overflow-hidden rounded-lg border shadow-md transition-[border-color,box-shadow,opacity,scale] duration-300",
        isPending && "border-warning-ink/40 ring-warning-ink/40 ring-1",
        !isPending && "opacity-80",
        disabled && "opacity-60",
        isExiting && "scale-95 opacity-0",
        isPending && "animate-in fade-in slide-in-from-right-4 duration-300",
        className
      )}
    >
      {/* Header */}
      <div className="border-border/50 bg-muted/30 flex items-center justify-between gap-2 border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="bg-primary/10 flex size-9 items-center justify-center rounded-md">
            <ToolIcon className="text-primary size-5" toolName={approval.tool} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="text-foreground font-semibold">{displayToolName}</h4>
              {showPosition && position && total && (
                <span className="bg-muted text-muted-foreground rounded-md px-2 py-0.5 text-xs font-medium">
                  {position}/{total}
                </span>
              )}
            </div>
          </div>
        </div>
        <Badge className={cn("text-xs", statusMeta.className)}>{statusMeta.label}</Badge>
      </div>

      {/* Summary */}
      <div className="border-border/50 border-b px-4 py-3">
        <p className="text-foreground text-sm">{formatSummary(approval.summary)}</p>
        {isPersonalGate && (
          <p className="text-muted-foreground mt-1 text-xs" data-testid="personal-gate-notice">
            {t("personalSources.search.approvalNotice", {
              defaultValue:
                "Your personal sources are part of this chat, so every action beyond reading needs your approval. Text in your mail or chats can try to steer the assistant; check what it is about to do.",
            })}
          </p>
        )}
      </div>

      {/* Parameters */}
      <div className="px-4 py-3">
        {argumentsHidden ? (
          <div className="border-warning-ink/40 bg-warning/30 text-warning-ink rounded-md border px-3 py-2 text-sm">
            Arguments are too large to display and cannot be safely reviewed. Deny this request — it
            cannot be approved without seeing what it will do.
          </div>
        ) : (
          <>
            <h5 className="text-muted-foreground mb-2 text-xs font-medium tracking-wide uppercase">
              {getParametersSectionTitle(approval.arguments)}
            </h5>
            {isPersonalGate ? (
              <PlainToolArguments args={approval.arguments} />
            ) : (
              <ContextualParametersDisplay args={approval.arguments} />
            )}
          </>
        )}
      </div>

      {/* Raw JSON (collapsible) */}
      <Collapsible
        className="border-border/50 border-t"
        open={showRawJson}
        onOpenChange={setShowRawJson}
      >
        <CollapsibleTrigger asChild>
          <button
            className="text-muted-foreground hover:bg-muted/50 flex w-full items-center justify-between px-4 py-2 text-xs transition-colors"
            type="button"
          >
            <span>View raw JSON</span>
            <ChevronDown
              className={cn("size-4 transition-transform", showRawJson && "rotate-180")}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <pre className="bg-muted/30 text-muted-foreground max-h-48 overflow-auto px-4 py-3 text-xs">
            {formattedArguments}
          </pre>
        </CollapsibleContent>
      </Collapsible>

      {/* Error message */}
      {errorMessage && (
        <div className="border-destructive/20 bg-destructive/10 text-destructive border-t px-4 py-2 text-sm">
          {errorMessage}
        </div>
      )}

      {/* Denial reason */}
      {approval.reason && (
        <div className="border-border/50 bg-muted/30 text-muted-foreground border-t px-4 py-2 text-sm italic">
          Reason: {approval.reason}
        </div>
      )}

      {/* Footer with actions and countdown */}
      <div className="border-border/50 bg-muted/20 flex items-center justify-between gap-3 border-t px-4 py-3">
        <div className="flex items-center gap-2">
          <Button
            className="gap-1.5"
            disabled={!canApprove}
            size="sm"
            title={argumentsHidden ? "Arguments are hidden and cannot be approved" : undefined}
            onClick={() => void handleDecision(true)}
          >
            {isSubmitting ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Check className="size-4" />
            )}
            Approve
          </Button>
          <Button
            className="gap-1.5"
            disabled={!canTakeAction}
            size="sm"
            variant="outline"
            onClick={() => void handleDecision(false)}
          >
            {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : <X className="size-4" />}
            Deny
          </Button>
        </div>

        {countdown && (
          <div className="flex items-center gap-2">
            <div className="bg-border h-1.5 w-16 overflow-hidden rounded-full">
              <div
                style={{ transform: `scaleX(${countdown.progress / 100})` }}
                className={cn(
                  "h-full origin-left transition-[transform,background-color] duration-1000",
                  countdown.isExpiring && "bg-destructive-ink",
                  !countdown.isExpiring && countdown.progress < 50 && "bg-warning-ink",
                  !countdown.isExpiring && countdown.progress >= 50 && "bg-success-ink"
                )}
              />
            </div>
            <span
              className={cn(
                "flex items-center gap-1 text-xs font-medium tabular-nums",
                countdown.isExpiring ? "text-destructive" : "text-muted-foreground"
              )}
            >
              <Clock className="size-3" />
              {countdown.remainingSecs > 0 ? `${countdown.remainingSecs}s` : "Expired"}
            </span>
          </div>
        )}
      </div>

      {showShortcutsHint && (
        <div className="border-border/50 bg-muted/10 text-muted-foreground/70 border-t py-2 text-center text-xs">
          Shortcuts: Enter/A = Approve • D = Deny
        </div>
      )}
    </div>
  )
}
